type RouteMap = Readonly<Record<string, string>>

type NavigationItem = {
  type?: string
  route?: string
  href?: string
  subRoutes?: readonly string[]
  subNavItems?: readonly NavigationItem[]
}

type NavigationDomain = {
  id: string
  navigationEntries: readonly { subNavItems: readonly NavigationItem[] }[]
}

export function buildRouteCatalog(
  modules: Record<string, RouteMap>,
  coreRoutes: RouteMap = {}
): Record<string, string> {
  const routes: Record<string, string> = {}
  const owners = new Map<string, string>()
  for (const [source, patterns] of [['core', coreRoutes], ...Object.entries(modules)] as const) {
    if (!patterns || typeof patterns !== 'object' || Array.isArray(patterns)) {
      throw new Error(`${source}: expected a named routes object`)
    }
    for (const [name, pattern] of Object.entries(patterns)) {
      if (typeof pattern !== 'string' || !pattern) {
        throw new Error(`${source}: route "${name}" must have a non-empty URL pattern`)
      }
      const owner = owners.get(name)
      if (owner !== undefined && routes[name] !== pattern) {
        throw new Error(`Conflicting route "${name}" in ${owner} and ${source}`)
      }
      routes[name] = pattern
      owners.set(name, source)
    }
  }
  return routes
}

export function assertNavigationRoutes(
  domains: readonly NavigationDomain[],
  routes: RouteMap
): void {
  const missing: string[] = []
  const visit = (id: string, entries: readonly NavigationItem[]): void => {
    for (const entry of entries) {
      if (
        !entry.href &&
        entry.route &&
        !Object.prototype.hasOwnProperty.call(routes, entry.route)
      ) {
        missing.push(`${id}: ${entry.route}`)
      }
      for (const name of entry.subRoutes ?? []) {
        if (!Object.prototype.hasOwnProperty.call(routes, name)) missing.push(`${id}: ${name}`)
      }
      if (entry.subNavItems) visit(id, entry.subNavItems)
    }
  }
  for (const domain of domains) {
    for (const section of domain.navigationEntries) visit(domain.id, section.subNavItems)
  }
  if (missing.length) throw new Error(`Unresolved navigation routes:\n${missing.join('\n')}`)
}
